import { getSessionStorageItem } from 'businesslayer/minutesLocalStore'
import {
    Identify as AmplitudeIdentify,
    Types as AmplitudeTypes,
    identify as amplitudeIdentify,
    init as amplitudeInit,
    setGroup as amplitudeSetGroup,
    track as amplitudeTrack
} from '@amplitude/analytics-browser'
import pendoRuleToAmplitudeEventName from 'common/analytics/pendoFeatureMap.json'
import { all, keys } from 'rambdax'

const pendoFeatureMap = pendoRuleToAmplitudeEventName as Record<string, string>
export const ANALYTICS_RETRY_DELAY_MS = 500
export const ANALYTICS_MAX_RETRIES = 20
export const AMPLITUDE_ALLOWED_USER_PROPERTIES = [
    'appId',
    'id',
    'lang',
    'language',
    'platform',
    'region',
    'subRegion',
    'superRegion',
    'blcTier',
    'role',
    'environment'
] as const
const AMPLITUDE_ALLOWED_USER_PROPERTIES_SET = new Set<string>(AMPLITUDE_ALLOWED_USER_PROPERTIES)

function resolveAmplitudeDisplayEventName(ruleOrEventName: string): string | undefined {
    return pendoFeatureMap[ruleOrEventName]
}

function warnUnmappedAmplitudeEvent(
    ruleOrEventName: string,
    source: 'data-analytics' | 'pendoTrack'
): void {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'production') {
        return
    }
    console.warn(
        `[Amplitude] Skipped unmapped ${source} event tag: "${ruleOrEventName}". Add it to pendoFeatureMap.json to allow tracking.`
    )
}

function warnAnalyticsRetryExhausted(eventName: string, retries: number, source: string): void {
    console.warn(
        `[Analytics] Dropped event "${eventName}" after ${retries} retries (${source} unavailable).`
    )
}

function warnDroppedAmplitudeIdentifyProperties(droppedKeys: string[]): void {
    if (!droppedKeys.length) return
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'production') {
        return
    }
    console.warn(
        `[Amplitude] Dropped non-allowlisted identify properties: ${droppedKeys.join(', ')}`
    )
}

function filterAllowlistedAmplitudeIdentifyProperties(
    metadata: Record<string, unknown>
): {
    allowed: Record<string, unknown>
    droppedKeys: string[]
} {
    const allowed: Record<string, unknown> = {}
    const droppedKeys: string[] = []
    Object.entries(metadata).forEach(([key, value]) => {
        if (!AMPLITUDE_ALLOWED_USER_PROPERTIES_SET.has(key)) {
            droppedKeys.push(key)
            return
        }
        allowed[key] = value
    })
    return { allowed, droppedKeys }
}

// Keyboard key codes
export const KEY_CODES = {
    ENTER: 13,
    SPACE: 32
} as const

/**
 * Debounce a function by {delay} milliseconds
 * @param fn
 * @param delay
 */
export const debounce = (fn: Function, delay: number) => {
    let timer: any = null
    return function (...args) {
        const context = this
        timer && clearTimeout(timer)
        timer = setTimeout(() => {
            fn.apply(context, args)
        }, delay)
    }
}

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export const remove_non_ascii = (str) => {
    if (str === null || str === '') return false

    const asString = str.toString()

    // Remove all non ascii chars in the string
    return asString.replace(/[^\x20-\x7E]/g, '')
}

export const refit_keys = (obj, changeFn, shallow = false) => {
    const build = {}
    for (const key in obj) {
        // Get the destination key
        const destKey = changeFn(key) || key

        // Get the value
        const value = obj[key]
        const isValueArray = Array.isArray(value)
        const isValueObject = typeof value === 'object'

        const refited_value =
            isValueArray && !shallow
                ? value.map((i) => refit_keys(i, changeFn))
                : isValueObject && !shallow
                ? refit_keys(value, changeFn)
                : value

        // Set it on the result using the destination key
        build[destKey] = refited_value
    }
    return build
}

export const snake_to_camel_case = (string: string) => {
    const REGEX = /(_[a-zA-Z])/g
    return string.replace(REGEX, (match) => match[1].toUpperCase())
}

export const getRandomInt = (max: number) => {
    return Math.floor(Math.random() * Math.floor(max))
}

export const getRandomHexString = (max: number) => {
    return getRandomInt(max).toString(16)
}

export const allObjectValues = <T = any>(fn: (value: T) => boolean, obj: Record<string, T>) => {
    const withValue = (obj_key) => fn(obj[obj_key])
    return all(withValue, keys(obj))
}

export const addKeyPressHandler = (event: React.KeyboardEvent<any>) =>
    event.which === KEY_CODES.ENTER || event.which === KEY_CODES.SPACE

/**
 * Truncate a string to a specified length and add ellipsis if truncated
 * @param str - The string to truncate
 * @param maxLength - Maximum length before truncation (default: 120)
 * @param ellipsis - The ellipsis string to append (default: '...')
 * @returns The truncated string with ellipsis if needed
 */
export const truncateString = (
    str: string | undefined | null,
    maxLength: number = 120,
    ellipsis: string = '...'
): string => {
    if (!str || typeof str !== 'string') {
        return str || ''
    }

    if (str.length <= maxLength) {
        return str
    }

    return str.slice(0, maxLength) + ellipsis
}

export const handleLinkFocus = (e: React.FocusEvent<HTMLAnchorElement>) => {
    e.target.style.clip = 'auto' /* unclip the link to show it when it receives focus */
}

export const handleLinkBlur = (e: React.FocusEvent<HTMLAnchorElement>) => {
    e.target.style.clip = 'rect(0 0 0 0)' /* clip the link again to hide it when it loses focus */
}

export const ContentContainerhandleFocus = () => {
    const contentBody = document.getElementById('ContentContainer_ContentBody') as HTMLElement
    if (contentBody) {
        contentBody.tabIndex = 0
        contentBody.focus()
    }
}

let dataAnalyticsAmplitudeBridgeInstalled = false
let lastDataAnalyticsEmit: { tag: string; t: number } = { tag: '', t: 0 }
const INPUT_BEARING_SELECTOR = 'input, textarea, select, [contenteditable]'

/**
 * Central guardrail list for areas that must never emit analytics from global DOM listeners.
 * Add selectors here for future sensitive/regulatory screens without changing listener logic.
 */
export const DATA_ANALYTICS_EXCLUDED_SELECTORS = [
    '[data-analytics-exclude]',
    '[data-sensitive-workflow]',
    '[data-regulated-workflow]',
    '[data-security-sensitive]'
] as const

function dedupeDataAnalyticsEmit(tag: string): boolean {
    const now = Date.now()
    if (lastDataAnalyticsEmit.tag === tag && now - lastDataAnalyticsEmit.t < 200) {
        return false
    }
    lastDataAnalyticsEmit = { tag, t: now }
    return true
}

function isContentEditableElement(element: Element): boolean {
    if (!(element instanceof HTMLElement)) return false
    if (element.isContentEditable) return true
    const attr = element.getAttribute('contenteditable')
    return attr !== null && attr !== 'false'
}

function isInputBearingElement(element: Element): boolean {
    if (element.matches(INPUT_BEARING_SELECTOR) && isContentEditableElement(element)) return true
    if (element.matches('textarea, select')) return true
    if (element.matches('input')) {
        const type = (element.getAttribute('type') || '').toLowerCase()
        // Explicitly keep sensitive/control fields out of global tracking.
        if (type === 'password' || type === 'hidden' || type === 'file') return true
        return true
    }
    return false
}

function isSafeTogglableInput(element: Element): boolean {
    if (!element.matches('input')) return false
    const type = (element.getAttribute('type') || '').toLowerCase()
    if (type !== 'checkbox' && type !== 'radio') return false
    return !!element.getAttribute('data-analytics')?.trim()
}

function isExplicitlyTaggedAnalyticsControl(element: Element): boolean {
    if (!element.matches('input, textarea, select')) return false
    return !!element.getAttribute('data-analytics')?.trim()
}

function isExcludedByCentralizedSelectorList(element: Element): boolean {
    return DATA_ANALYTICS_EXCLUDED_SELECTORS.some((selector) => !!element.closest(selector))
}

function shouldSkipAnalyticsCapture(target: EventTarget | null, analyticsHost: Element): boolean {
    if (!(target instanceof Element)) return true
    if (
        isExcludedByCentralizedSelectorList(target) ||
        isExcludedByCentralizedSelectorList(analyticsHost)
    ) {
        return true
    }
    // Allow explicitly-tagged checkbox/radio controls (e.g., Switch/Checkbox inputProps)
    // and attendance/search inputs that carry data-analytics directly on the control.
    if (isSafeTogglableInput(target) || isSafeTogglableInput(analyticsHost)) {
        return false
    }
    if (
        isExplicitlyTaggedAnalyticsControl(target) ||
        isExplicitlyTaggedAnalyticsControl(analyticsHost)
    ) {
        return false
    }
    if (
        isInputBearingElement(target) ||
        isInputBearingElement(analyticsHost) ||
        !!target.closest(INPUT_BEARING_SELECTOR)
    ) {
        return true
    }
    return false
}

function buildDataAnalyticsInteractionHandler(
    interaction: 'click' | 'change',
    emit: (ruleOrEventName: string, payload: Record<string, unknown>) => void
) {
    return (ev: Event): void => {
        const rawTarget = ev.target
        if (!rawTarget || !(rawTarget instanceof Element)) return
        const host = rawTarget.closest('[data-analytics]')
        if (!host) return
        if (shouldSkipAnalyticsCapture(rawTarget, host)) return
        const tag = host.getAttribute('data-analytics')?.trim()
        if (!tag) return
        if (!resolveAmplitudeDisplayEventName(tag)) {
            warnUnmappedAmplitudeEvent(tag, 'data-analytics')
            return
        }
        if (!window.amplitudeAnalyticsEnabled) return
        if (!dedupeDataAnalyticsEmit(tag)) return
        emit(tag, {
            interaction,
            source: 'data-analytics',
            domEvent: ev.type
        })
    }
}

/**
 * Pendo uses `data-analytics` across the UI; it picks those up in-product.
 * Mirror data-analytics DOM interactions to Amplitude using the explicit allowlist from
 * pendoFeatureMap.json. Unmapped tags are intentionally dropped (dev-only warning).
 * Global listeners are intentionally centralized here so security exclusions and approvals
 * are enforced consistently across the application.
 */
function installDataAnalyticsAmplitudeBridge(): void {
    if (dataAnalyticsAmplitudeBridgeInstalled || typeof document === 'undefined') {
        return
    }
    dataAnalyticsAmplitudeBridgeInstalled = true

    document.addEventListener(
        'click',
        buildDataAnalyticsInteractionHandler('click', amplitudeTrackOnce),
        true
    )
    document.addEventListener(
        'change',
        buildDataAnalyticsInteractionHandler('change', amplitudeTrackOnce),
        true
    )
}

export const __testing__ = {
    buildDataAnalyticsInteractionHandler,
    shouldSkipAnalyticsCapture,
    filterAllowlistedAmplitudeIdentifyProperties,
    resetDataAnalyticsBridgeState: () => {
        dataAnalyticsAmplitudeBridgeInstalled = false
        lastDataAnalyticsEmit = { tag: '', t: 0 }
    }
}

/** Product key for Amplitude user properties (org BW integration overview). */
function getAmplitudeAppId(): string {
    return 'minutes-web'
}

function resolveAmplitudeEnvironmentGroup(): string {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'production') {
        return 'production'
    }
    return 'development'
}

function applyAmplitudeGroupEnvironment(): void {
    try {
        amplitudeSetGroup('environment', resolveAmplitudeEnvironmentGroup())
    } catch {
        /* no-op */
    }
}

function applyAmplitudeIdentify(
    visitor: Record<string, unknown>,
    account: Record<string, unknown>
) {
    try {
        const id = new AmplitudeIdentify()
        id.set('appId', getAmplitudeAppId())
        const merged = { ...visitor, ...account }
        const { allowed, droppedKeys } = filterAllowlistedAmplitudeIdentifyProperties(merged)
        warnDroppedAmplitudeIdentifyProperties(droppedKeys)
        Object.entries(allowed).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') return
            if (k === 'appId') return
            id.set(k, v as string | number | boolean)
        })
        amplitudeIdentify(id)
    } catch {
        /* no-op */
    }
}

type ProductAnalyticsSettings = {
    enable_admin?: boolean
    enable_director?: boolean
}

/** Octopus → container env → env.sh → window._env_ (same pattern as CKEDITOR_LICENSE_KEY). */
function resolveConfiguredEnvValue(
    value: string | undefined,
    placeholder: string
): string | undefined {
    const trimmed = value?.trim()
    if (!trimmed || trimmed === placeholder) {
        return undefined
    }
    return trimmed
}

function resolveAmplitudeApiKey(): string | undefined {
    // Public browser ingestion key (Amplitude client SDK key), not a confidential server secret.
    // This is runtime-injected into `window._env_` on purpose for browser-side initialization.
    return resolveConfiguredEnvValue(window._env_?.AMPLITUDE_API_KEY, 'AMPLITUDE_API_KEY')
}

function resolveAmplitudeServerZone(): string | undefined {
    return resolveConfiguredEnvValue(window._env_?.AMPLITUDE_SERVER_ZONE, 'AMPLITUDE_SERVER_ZONE')
}

function initAmplitudeAnalytics(
    pendoMeta: { visitor: Record<string, unknown>; account: Record<string, unknown> },
    onComplete?: () => void
) {
    const complete = () => onComplete?.()

    if (window.amplitudeSdkInitialized) {
        complete()
        return
    }

    try {
        const apiKey = resolveAmplitudeApiKey()
        if (!apiKey) {
            complete()
            return
        }

        // BW Integration (Confluence: Amplitude BW Integration Developer Overview):
        // programmatic events only — same pendoTrack / data-analytics → track() fan-out; no SDK autocapture / remote config.
        const initOptions: AmplitudeTypes.BrowserOptions = {
            fetchRemoteConfig: false,
            autocapture: false,
            defaultTracking: false
        }
        const serverZoneRaw = resolveAmplitudeServerZone()
        if (serverZoneRaw && String(serverZoneRaw).toUpperCase() === 'EU') {
            initOptions.serverZone = 'EU'
        }
        amplitudeInit(apiKey, initOptions)
        window.amplitudeSdkInitialized = true
        window.amplitudeAnalyticsEnabled = true
        window.amplitudeMetadata = {
            visitor: { ...pendoMeta.visitor },
            account: { ...pendoMeta.account }
        }
        applyAmplitudeIdentify(window.amplitudeMetadata.visitor, window.amplitudeMetadata.account)
        applyAmplitudeGroupEnvironment()
        installDataAnalyticsAmplitudeBridge()
    } catch {
        /* no-op */
    }
    complete()
}

function amplitudeTrackOnce(eventName: string, data?: Record<string, unknown>) {
    const attempt = (retryCount = 0) => {
        if (!window.productAnalyticsConfigured) {
            if (retryCount >= ANALYTICS_MAX_RETRIES) {
                warnAnalyticsRetryExhausted(eventName, retryCount, 'productAnalyticsConfigured')
                return
            }
            setTimeout(() => attempt(retryCount + 1), ANALYTICS_RETRY_DELAY_MS)
            return
        }
        if (!window.amplitudeAnalyticsEnabled) {
            return
        }
        try {
            const displayName = resolveAmplitudeDisplayEventName(eventName)
            if (!displayName) {
                warnUnmappedAmplitudeEvent(
                    eventName,
                    data?.source === 'data-analytics' ? 'data-analytics' : 'pendoTrack'
                )
                return
            }
            const payload = displayName !== eventName ? { ...data, pendo_rule: eventName } : data
            amplitudeTrack(displayName, payload)
            return
        } catch {
            /* no-op */
        }
        if (retryCount >= ANALYTICS_MAX_RETRIES) {
            warnAnalyticsRetryExhausted(eventName, retryCount, 'amplitudeTrack')
            return
        }
        setTimeout(() => attempt(retryCount + 1), ANALYTICS_RETRY_DELAY_MS)
    }
    attempt()
}

export const pendoTrack = (eventName, data) => {
    let amplitudeEnqueued = false
    const run = (retryCount = 0) => {
        if (!amplitudeEnqueued) {
            amplitudeEnqueued = true
            amplitudeTrackOnce(eventName, data)
        }
        if (window.pendo && window.pendo.isReady && window.pendo.isReady()) {
            window.pendo.track(eventName, data)
        } else {
            if (retryCount >= ANALYTICS_MAX_RETRIES) {
                warnAnalyticsRetryExhausted(eventName, retryCount, 'pendo')
                return
            }
            setTimeout(() => run(retryCount + 1), ANALYTICS_RETRY_DELAY_MS)
        }
    }
    run()
}

export const rounded = (count) => {
    return Math.round(count / 100) * 100
}

function getElementDefaultDisplay(tag) {
    var cStyle,
        t = document.createElement(tag),
        gcs = 'getComputedStyle' in window

    document.body.appendChild(t)
    cStyle = (gcs ? window.getComputedStyle(t, '') : t.currentStyle).display
    document.body.removeChild(t)

    return cStyle
}

export function removeTags(htmlString) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlString, 'text/html')
    const body = doc.body

    let strippedText = ''

    function processNode(node) {
        const nodeType = node.nodeType

        if (nodeType === Node.TEXT_NODE) {
            strippedText += node.textContent
        } else if (nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase()

            if (getElementDefaultDisplay(tagName) === 'inline') {
                for (let i = 0; i < node.childNodes.length; i++) {
                    processNode(node.childNodes[i])
                }
            } else {
                strippedText += ' '
                for (let i = 0; i < node.childNodes.length; i++) {
                    processNode(node.childNodes[i])
                }
                strippedText += ' '
            }
        }
    }

    processNode(body)

    return strippedText.replace(/\s+/g, ' ').trim()
}

/**
 * Initialize Pendo
 * This function initializes the Pendo analytics tool, but only for specific platforms.
 * The function checks if the current platform is "board" or "boardeffect", and only initializes Pendo for these platforms.
 * If platform is board then enable tracting based on CSP settings.
 * An empty visitor ID is used to generate a unique ID in the Pendo dashboard.
 */

export const initializePendo = (settings, language) => {
    window.productAnalyticsConfigured = false

    const markProductAnalyticsConfigured = () => {
        window.productAnalyticsConfigured = true
    }

    const {
        currentUser: { site_name },
        pendoAnalytics: pendo_analytics,
        amplitudeAnalytics: amplitude_analytics,
        accountId,
        userLanguage: lang
    } = settings

    // GetSettings `finalizeSettings` can set root `settings.platform` from session `parentPlatform`
    // (boardswebadmin / boardswebdirector) while `currentUser.platform` stays coarse or differs on some envs.
    let platform = settings.currentUser?.platform as Platform
    const rootPlatform = settings.platform as Platform | undefined
    if (!['boards', 'boardeffect', 'mock'].includes(platform)) {
        if (rootPlatform === 'mock') platform = 'mock'
        else if (rootPlatform === 'boardeffect') platform = 'boardeffect'
        else if (
            rootPlatform === 'boards' ||
            rootPlatform === 'boardswebadmin' ||
            rootPlatform === 'boardswebdirector'
        ) {
            platform = 'boards'
        }
    }
    if (!['boards', 'boardeffect', 'mock'].includes(platform)) {
        markProductAnalyticsConfigured()
        return
    }

    let region = settings?.region || 'None'
    let subRegion = settings?.subRegion || 'None'
    let superRegion = settings?.superRegion || 'None'
    let blcTier = settings?.blcTier || 'None'

    if (platform === 'boardeffect') {
        ;[region, subRegion, superRegion, blcTier] = [...Array(4).fill('None')]
    }

    const platformDetails = {
        boards: getSessionStorageItem('parentPlatform') || '',
        boardeffect: 'boardeffect',
        mock: 'mock'
    }

    const getPlatformDetails = (platform) => platformDetails[platform]
    const getId = () => {
        const key = getPlatformDetails(platform)
        const idMapping = {
            boardswebdirector: 'board-web-director',
            boardswebadmin: accountId,
            boardeffect: site_name,
            mock: 'mock'
        }
        const mapped = idMapping[key]
        if (mapped !== undefined && mapped !== null && mapped !== '') {
            return mapped
        }
        if (platform === 'boards' && accountId) {
            return accountId
        }
        return undefined
    }

    const shouldInitializeProductAnalytics = (analyticsSettings: ProductAnalyticsSettings) => {
        const platformDetails = getPlatformDetails(platform)
        const bothBoardsAnalyticsOn = !!(
            analyticsSettings?.enable_admin && analyticsSettings?.enable_director
        )

        // Boards Web: empty sessionStorage parentPlatform yields '' / undefined getId();
        // still allow init when tenant has both client flags on (server already decided).
        if (!platformDetails && !getId() && !bothBoardsAnalyticsOn) {
            return false
        }
        return (
            bothBoardsAnalyticsOn ||
            platformDetails === 'boardeffect' ||
            (platformDetails === 'boardswebadmin' && analyticsSettings?.enable_admin) ||
            (platformDetails === 'boardswebdirector' && analyticsSettings?.enable_director) ||
            platformDetails === 'mock'
        )
    }

    const buildProductAnalyticsMeta = () => {
        const pendoObj: PendoInitalize = {
            visitor: { id: '', lang, language },
            account: {
                id: getId(),
                platform: getPlatformDetails(platform) || platform,
                region,
                superRegion,
                subRegion,
                blcTier
            },
            excludeAllText: true
        }

        if (getPlatformDetails(platform) === 'boardswebdirector') {
            const account = Object.assign({}, pendoObj.account)
            delete account.id

            return {
                visitor: {
                    ...pendoObj.visitor,
                    ...account
                },
                account: {
                    id: getId()
                },
                excludeAllText: true
            }
        }
        return pendoObj
    }

    const pendoWouldInit = shouldInitializeProductAnalytics(pendo_analytics)
    const amplitudeWouldInit = shouldInitializeProductAnalytics(amplitude_analytics)
    const pendoNeedsInit = !!(pendoWouldInit && !window.pendo?.isInitialized)
    const amplitudeNeedsInit = !!(amplitudeWouldInit && !window.amplitudeSdkInitialized)

    if (pendoWouldInit || amplitudeWouldInit) {
        const meta = buildProductAnalyticsMeta()

        if (pendoNeedsInit) {
            window.pendo.isInitialized = pendoWouldInit
            window.pendo.metaData = meta
            window.pendo.initialize(meta)
        }

        if (amplitudeNeedsInit) {
            initAmplitudeAnalytics(
                { visitor: meta.visitor, account: meta.account },
                markProductAnalyticsConfigured
            )
        } else {
            markProductAnalyticsConfigured()
        }
        return
    }

    markProductAnalyticsConfigured()
}

/**
 * Update pendo metadata
 * if metadata need to update in visitor then pass isVisitior as true
 */
export const updatePendoMetadata = (value, isVisitior: boolean) => {
    const pendoReady = !!(
        window.pendo?.isInitialized &&
        typeof window.pendo?.isReady === 'function' &&
        window.pendo.isReady()
    )
    if (pendoReady && window.pendo?.metaData) {
        const { visitor, account } = window.pendo.metaData
        const updatedData = isVisitior ? { ...visitor, ...value } : { ...account, ...value }
        const updateOption = isVisitior
            ? { ...window.pendo.metaData, visitor: updatedData }
            : { ...window.pendo.metaData, account: updatedData }
        window.pendo.metaData = updateOption
        window.pendo.updateOptions(updateOption)
    } else if (window.pendo?.isInitialized) {
        console.warn('Pendo is not initialized')
    }

    if (window.amplitudeAnalyticsEnabled && window.amplitudeMetadata) {
        try {
            const { visitor, account } = window.amplitudeMetadata
            const updatedData = isVisitior ? { ...visitor, ...value } : { ...account, ...value }
            window.amplitudeMetadata = isVisitior
                ? { ...window.amplitudeMetadata, visitor: updatedData }
                : { ...window.amplitudeMetadata, account: updatedData }
            applyAmplitudeIdentify(
                window.amplitudeMetadata.visitor,
                window.amplitudeMetadata.account
            )
        } catch {
            /* no-op */
        }
    }
}

export interface PendoInitalize {
    visitor: {
        id: string
        lang: string
        language: string
        platform?: string
        region?: string
        superRegion?: string
        subRegion?: string
        blcTier?: string
    }
    account: {
        id?: string
        platform?: string
        role?: string
        region?: string
        superRegion?: string
        subRegion?: string
        blcTier?: string
    }
    excludeAllText: boolean
}

export interface LengthStats {
    avgCharCount: number
    avgWordCount: number
    minCharCount: number
    minWordCount: number
    maxCharCount: number
    maxWordCount: number
}

export function calculateLength(minutesSections) {
    let charCount = 0
    let secCount = 0
    let wordCount = 0
    let charLen: Record<string, number> = {}
    let wordLen: Record<string, number> = {}
    const sectionLists = minutesSections?.minutesSections

    for (let k in sectionLists) {
        const section = sectionLists[k]

        if (
            section.attributes.sectionType === 'minutes' &&
            section?.attributes?.htmlBody !== null
        ) {
            secCount++
            const htmlStr = section.attributes.htmlBody
            const cleanedString = removeTags(htmlStr)
            const newStr = htmlStr
                ?.replace(/(<([^>]+)>)/gi, '')
                .split('&nbsp;')
                .join('')

            if (cleanedString.length > 0) {
                wordCount = wordCount + cleanedString.split(' ').length

                charCount = charCount + newStr.length
                charLen = { ...charLen, [k]: newStr.length }
                wordLen = { ...wordLen, [k]: cleanedString.split(' ').length }
            } else {
                charLen = { ...charLen, [k]: newStr.length }
                wordLen = { ...wordLen, [k]: cleanedString.length }
            }
        }
    }
    const avgCharCount = Math.ceil(charCount / secCount)
    const avgWordCount = Math.ceil(wordCount / secCount)

    const charValues = Object.values(charLen)
    const wordValues = Object.values(wordLen)

    const minCharCount = Math.min(...charValues)
    const minWordCount = Math.min(...wordValues)

    const maxCharCount = Math.max(...charValues)
    const maxWordCount = Math.max(...wordValues)

    return {
        avgCharCount,
        avgWordCount,
        minCharCount,
        minWordCount,
        maxCharCount,
        maxWordCount
    }
}
export const calculateRestrictedPendoValues = (
    reviewersList: PersonChip[],
    payload: any,
    totalNumberOfSections: number
) => {
    const isNewReview = reviewersList.length === 0
    const restrictedReviewers: PersonChip[] = payload.recipients.filter((it) => !it.is_general)
    const restrictedSecLength: number[] = restrictedReviewers.map((it) => it.section_ids.length - 1)
    const maximumSectionsPermitted =
        restrictedSecLength.length > 0 ? Math.max(...restrictedSecLength) : 0
    const restrictedPendoObj = {
        isNewReview: isNewReview,
        generalReviewersCount: payload.recipients.length - restrictedReviewers.length,
        restrictedReviewersCount: restrictedReviewers.length,
        totalNumberOfSections: totalNumberOfSections - 1,
        maximumSectionsPermitted: maximumSectionsPermitted
    }
    return restrictedPendoObj
}
/**
 * @description Filters an array of meeting objects to include only those meetings that fall within a specified date range.
 * @param {string} startDateStr - The start date of the date range in string format.
 * @param {string} endDateStr - The end date of the date range in string format.
 * @param {MinutesUnnormalized[]} datesArray - An array of meeting objects, each containing an array of meeting dates.
 * @returns {MinutesUnnormalized[]} An array of meeting objects that have at least one meeting date within the specified range.
 */

export function filterDatesBetween(
    startDateStr: string,
    endDateStr: string,
    datesArray: MinutesUnnormalized[]
): MinutesUnnormalized[] {
    const filteredMeetings: MinutesUnnormalized[] = []
    const startDate = new Date(startDateStr)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(endDateStr)
    endDate.setHours(23, 59, 59, 999)

    for (const meeting of datesArray) {
        for (const meetingDate of meeting.meeting_dates) {
            const startDateString = meetingDate.start_date || ''
            const currentDate = new Date(startDateString)
            if (currentDate >= startDate && currentDate <= endDate) {
                filteredMeetings.push(meeting)
                break
            }
        }
    }

    return filteredMeetings
}
